import { SocketEvent } from "@common/SocketEvents";
import { useAppContext } from "./App.context";
import { useSocket } from "./Socket.context";

// The PIN last used successfully in this tab, so "Stay admin on this device"
// can remember it when switched on after unlocking.
let lastPin: string | undefined;

// Admin state for this device.
//
// The Go server owns it: a room may have a 4-digit admin PIN, and only
// connections unlocked with it are admin (no PIN = everyone is). The Nest
// server has no PIN support and never sends ADMIN_STATUS; there we fall back
// to the old per-device "Administrator Override" switch.
//
// On the Go server that same administratorOverride flag means "this device is
// admin":
// - Room with a PIN: the PIN is remembered in adminPins and the socket context
//   unlocks with it on every connect ("stay admin on this device"). Devices
//   that had the old switch on remember the PIN the next time it's entered.
// - Room without a PIN: the device unlocks with an empty PIN (the server
//   accepts that while there is no PIN), so the guest permission switches
//   don't apply to it. Setting a PIN ends that; the PIN is needed from then.
export const useAdmin = () => {
  const { adminStatus, sendRequest, roomId } = useSocket();
  const { data, setData } = useAppContext();

  const supported = adminStatus !== null;
  const stayAdmin = !!data.administratorOverride;

  const rememberPin = (pin: string | undefined) =>
    setData("adminPins", (pins = {}) => {
      const next = { ...pins };
      if (pin) next[roomId] = pin;
      else delete next[roomId];
      return next;
    });

  return {
    supported,
    isAdmin: supported ? adminStatus.isAdmin : data.administratorOverride,
    hasAdminPassword: !!adminStatus?.hasAdminPassword,
    // The guest permission switches don't apply to this device. Without a PIN
    // everyone may edit settings but the switches still apply to everyone;
    // only a PIN-unlocked device is exempt. Older Go servers don't send the
    // field: unlocked with a PIN means exempt there too.
    exempt: supported
      ? adminStatus.exemptFromRestrictions ??
        (adminStatus.isAdmin && adminStatus.hasAdminPassword)
      : !!data.administratorOverride,
    // Stays unlocked across reloads and skips the auto logout.
    stayAdmin,
    setStayAdmin: (on: boolean) => {
      setData("administratorOverride", on);
      rememberPin(on ? lastPin ?? data.adminPins?.[roomId] : undefined);
      if (supported && !adminStatus.hasAdminPassword) {
        sendRequest(
          on ? SocketEvent.ADMIN_LOGIN : SocketEvent.ADMIN_LOGOUT,
          on ? { password: "" } : null
        );
      }
    },
    login: async (pin: string) => {
      const { ok } = await sendRequest(SocketEvent.ADMIN_LOGIN, {
        password: pin,
      });
      if (ok) {
        lastPin = pin;
        if (stayAdmin) rememberPin(pin);
      }
      return ok;
    },
    logout: () => {
      rememberPin(undefined);
      return sendRequest(SocketEvent.ADMIN_LOGOUT, null);
    },
    setPassword: async (pin: string) => {
      const result = await sendRequest(SocketEvent.ADMIN_SET_PASSWORD, {
        password: pin,
      });
      if (!result.error) {
        lastPin = pin;
        if (stayAdmin) rememberPin(pin);
      }
      return result;
    },
  };
};
